/**
 * [web/auth] SSO sign-in and callback page.
 * Owns the authorization UI; authentication and session issuance remain server responsibilities.
 */
import { useEffect, useRef, useState } from 'react';
import upLabShip from '../assets/up-lab-ship.png';
import { ApiError, api, saveSession, type AuthResult } from '../lib/api.ts';
import { LoginArt } from './LoginArt.tsx';

/**
 * SSO 独立登录页 + 回调处理（/sso/login 与 /sso/callback 共用此组件）。
 *
 * 与账号密码登录刻意分家：这是「员工统一身份」的专属入口。直登就绪时
 * 本页展示自绘账密表单（方案二，提交到 /api/v1/auth/sso/direct-login），
 * 用户全程不离开本页；未就绪时进入即跳转 Casdoor 旧路径。回调把 code
 * 换成会话令牌后与密码登录走同一份 saveSession / onAuthed。
 *
 * 登录完成统一落在「登录成功」收尾页：桌面握手流程以「返回桌面端」
 * uplab:// 深链为主按钮（桌面端握手就绪也会自行抢前台，按钮兜底），
 * 页脚保留进入网页版的次级出口；纯 Web 流程倒计时自动回首页。
 * 已持有会话的访问（含刷新）不再重新发起登录 —— 否则登录成功后刷新
 * 会被弹回登录页。
 */

const WEB_JUMP_DELAY_S = 3;

interface DoneState {
  readonly displayName: string;
  /** true = 桌面握手流程，收尾页停留等待用户返回桌面端 */
  readonly desktop: boolean;
}

export function SsoAuth({
  onAuthed,
}: {
  onAuthed: (r?: AuthResult) => void;
}) {
  // 地址栏派生值必须冻结在首次渲染：effect 会 replaceState 清掉查询串，
  // 而已登录浏览器上 App 的 loadMe 会触发重渲染 —— 若按渲染重算，
  // desktopHandshake 会变 null、claim 被 cancel，浏览器被带回首页，
  // 桌面端永远等不到握手（isCallback 同理，回调页清串后不能变登录页）。
  const [isCallback] = useState(() => window.location.pathname.startsWith('/sso/callback'));
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<DoneState | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(WEB_JUMP_DELAY_S);
  const [ssoUser, setSsoUser] = useState('');
  const [ssoPass, setSsoPass] = useState('');
  const [busy, setBusy] = useState(false);
  /** null = 形态未定（读取中）；false = 跳转流，true = 本页自绘表单。 */
  const [directReady, setDirectReady] = useState<boolean | null>(null);
  /** 浏览器在 Casdoor 侧的既有会话身份（其他公司系统登录留下的）；null = 没有或未探测。 */
  const [casdoorAccount, setCasdoorAccount] = useState<{ name: string; displayName: string } | null>(
    null,
  );
  /** 用户点了「使用其它账户」后展开账密表单。 */
  const [showOtherAccount, setShowOtherAccount] = useState(false);
  // code 与 claim 都是一次性副作用：React 开发态 effect 双跑会把同一个
  // code / handshake 消费两次（第二次必败，误报错误盖住成功页）
  const exchanged = useRef(false);
  const claimed = useRef(false);
  const [desktopHandshake] = useState<string | null>(() =>
    isCallback
      ? sessionStorage.getItem('uplab_desktop_handshake')
      : new URLSearchParams(window.location.search).get('desktopHandshake'),
  );

  const finishDesktopHandshake = async (handshake: string): Promise<void> => {
    if (claimed.current) return;
    claimed.current = true;
    await api.claimDesktopHandshake(handshake);
    sessionStorage.removeItem('uplab_desktop_handshake');
  };

  useEffect(() => {
    if (isCallback && !exchanged.current) {
      exchanged.current = true;
      const q = new URLSearchParams(window.location.search);
      const code = q.get('code');
      const state = q.get('state');
      if (code === null || state === null) {
        setError('回调缺少 code 或 state，请从登录页重新发起');
        return;
      }
      // 先清查询串：失败的 code 留在地址栏会诱导用户刷新重放
      window.history.replaceState(null, '', '/sso/login');
      api
        .ssoCallback(code, state)
        .then(async (r) => {
          saveSession(r);
          if (desktopHandshake !== null) {
            await finishDesktopHandshake(desktopHandshake);
            setDone({ displayName: r.user.displayName, desktop: true });
            onAuthed(r);
            return;
          }
          setDone({ displayName: r.user.displayName, desktop: false });
          onAuthed(r);
        })
        .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
    }
  }, [desktopHandshake, isCallback, onAuthed]);

  /** 本页自绘登录提交：账密 → direct-login → 与跳转流同一收尾。
   *  容器是 div（不是 form）：错误态/成功态与表单共享同一区域，
   *  用 form 会让嵌套状态里的按钮触发原生提交导致整页刷新。 */
  const submitDirect = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.ssoDirectLogin(ssoUser, ssoPass);
      saveSession(r);
      if (desktopHandshake !== null) {
        await finishDesktopHandshake(desktopHandshake);
        setDone({ displayName: r.user.displayName, desktop: true });
        onAuthed(r);
        return;
      }
      setDone({ displayName: r.user.displayName, desktop: false });
      onAuthed(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /** 选号免密直登：拿挑战 → 浏览器带 cookie 向 Casdoor 换 code（不带账密，
   *  Casdoor 用既有会话签发）→ 交回 /sso/callback 换发本系统会话。 */
  const continueAsAccount = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const challenge = await api.ssoDirectChallenge();
      const code = await api.ssoBrowserCode(challenge.casdoorBaseUrl, challenge, null);
      const r = await api.ssoCallback(code, challenge.state);
      saveSession(r);
      if (desktopHandshake !== null) {
        await finishDesktopHandshake(desktopHandshake);
        setDone({ displayName: r.user.displayName, desktop: true });
        onAuthed(r);
        return;
      }
      setDone({ displayName: r.user.displayName, desktop: false });
      onAuthed(r);
    } catch (err) {
      // 会话可能已失效（Cookie 过期/他处登出）—— 落回账密表单让人重输
      setError(err instanceof Error ? err.message : String(err));
      setCasdoorAccount(null);
      setShowOtherAccount(true);
    } finally {
      setBusy(false);
    }
  };

  /** 表单提交：容器 div 上兜底 Cmd/Ctrl+Enter。 */
  const onFormKeyDown = (e: React.KeyboardEvent): void => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void submitDirect();
    }
  };

  useEffect(() => {
    if (!isCallback && done === null && error === null && directReady === null) {
      let cancelled = false;
      let handshake = desktopHandshake;
      if (desktopHandshake !== null) {
        sessionStorage.setItem('uplab_desktop_handshake', desktopHandshake);
        window.history.replaceState(null, '', '/sso/login');
      }

      const startWebSso = (): void => {
        api
          .ssoStart()
          .then((r) => {
            if (!cancelled) window.location.assign(r.url);
          })
          .catch((err: unknown) => {
            if (!cancelled) setError(err instanceof Error ? err.message : String(err));
          });
      };

      // 未持会话才发起登录；已持会话（含登录成功后的刷新）直接进成功页。
      // 这里的 401 是预检的正常分支（浏览器里残留了过期 token），必须静默当
      // 「未登录」处理 —— 不能让它掉进全局 onUnauthorized 把 error 渲染出来。
      const decide = async (): Promise<void> => {
        const me = await api.me().catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 401) return { user: null };
          throw err;
        });
        if (cancelled) return;
        if (me.user !== null) {
          if (handshake !== null) {
            await finishDesktopHandshake(handshake);
            handshake = null;
            setDone({ displayName: me.user.displayName, desktop: true });
            return;
          }
          setDone({ displayName: me.user.displayName, desktop: false });
          return;
        }
        const status = await api.ssoStatus();
        if (cancelled) return;
        // 桌面握手必须走完整跳转页（Casdoor 页面承载回调），不受自绘影响
        if (status.directReady === true && handshake === null) {
          setDirectReady(true);
          // 探测浏览器在 Casdoor 侧的既有会话（其他公司系统登录留下的）。
          // 只读探测，失败一律静默 —— 有就用选号，没有就落账密表单。
          const challenge = await api.ssoDirectChallenge().catch(() => null);
          if (challenge !== null && !cancelled) {
            // GetAccount 成功形状：{status:'ok', name: 用户名, data: {displayName,...}}
            // 未登录：{status:'error', data:'Please login first'}
            const acc = (await api
              .ssoProbeSession(challenge.casdoorBaseUrl)
              .catch(() => null)) as {
              status?: string;
              name?: string;
              data?: { displayName?: string } | string | null;
            } | null;
            if (!cancelled && acc?.status === 'ok' && acc.name) {
              const data = acc.data;
              const displayName =
                typeof data === 'object' && data !== null && data.displayName
                  ? data.displayName
                  : acc.name;
              setCasdoorAccount({ name: acc.name, displayName });
            }
          }
          return;
        }
        startWebSso();
      };

      decide().catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });

      return () => {
        cancelled = true;
      };
    }
  }, [desktopHandshake, isCallback, done, error, directReady]);

  // 纯 Web 流程的收尾页倒计时；桌面流程停留不动等用户返回桌面端
  useEffect(() => {
    if (done === null || done.desktop) return;
    const t = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearInterval(t);
  }, [done]);

  useEffect(() => {
    if (done !== null && !done.desktop && secondsLeft <= 0) {
      window.location.replace('/');
    }
  }, [done, secondsLeft]);

  const showDirectForm = directReady === true && done === null;
  const showSuccess = done !== null;

  return (
    <div className="auth-page">
      <LoginArt />
      <div className="auth-card sso-card" onKeyDown={onFormKeyDown}>
        {/* ── 授权头：专业 IdP 惯例（Google/Okta "sign in to continue to X"）──
            上：双标横排（IdP logo → 目标应用），箭头表授权方向；
            下：一句话说明谁在向谁要凭据。品牌字用大写 UP-LAB（负责人 2026-09-24 要求）。 */}
        <div className="sso-grant">
          <span className="sso-grant-app">
            <img className="sso-grant-logo" src="/logo-up.png" alt="公司统一身份平台" />
            <span className="sso-grant-name">统一身份平台</span>
          </span>
          {/* 授权连接线：一条虚线轨道 + 两枚沿线流动的光点（CSS offset-path 驱动，
              reduced-motion 时退化为静态虚线）。这是「授权凭据从 IdP 流向应用」的隐喻。 */}
          <span className="sso-grant-link" aria-hidden="true">
            <svg width="72" height="20" viewBox="0 0 72 20" fill="none">
              <path
                id="sso-grant-track"
                d="M2 10 H70"
                stroke="var(--border)"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeDasharray="2 6"
              />
              <circle className="sso-pulse" r="2.6" fill="var(--fg)">
                <animateMotion dur="2.4s" repeatCount="indefinite" rotate="0">
                  <mpath href="#sso-grant-track" />
                </animateMotion>
              </circle>
              <circle className="sso-pulse sso-pulse-late" r="1.7" fill="var(--fg-3)">
                <animateMotion dur="2.4s" begin="1.2s" repeatCount="indefinite" rotate="0">
                  <mpath href="#sso-grant-track" />
                </animateMotion>
              </circle>
            </svg>
            <svg className="sso-grant-tip" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M4 2.5 9.5 7 4 11.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="sso-grant-app">
            <span className="sso-grant-mark" aria-hidden="true">
              <img className="sso-grant-symbol" src={upLabShip} alt="" />
            </span>
            <span className="sso-grant-name sso-grant-name-strong">UP-LAB</span>
          </span>
        </div>
        <p className="sso-grant-lead">
          {isCallback || done !== null ? (
            '正在处理统一身份登录'
          ) : showDirectForm && casdoorAccount !== null && !showOtherAccount ? (
            <>
              检测到<b>公司统一身份</b>已登录，选择账号以继续访问 UP-LAB
            </>
          ) : showDirectForm ? (
            <>
              使用<b>公司统一身份</b>登录以继续访问 UP-LAB
            </>
          ) : (
            '正在跳转到统一身份平台…'
          )}
        </p>

        {/* ── 主体：每个状态占满同一区域，卡片高度不跳 ── */}
        <div className="sso-body">
          {error !== null ? (
            <div className="sso-state">
              <div className="auth-error">{error}</div>
              <button
                type="button"
                className="btn-line sso-state-btn"
                onClick={() => {
                  setError(null);
                  setDirectReady(null); // 重新走一次形态判定
                }}
              >
                重新开始
              </button>
            </div>
          ) : showDirectForm && casdoorAccount !== null && !showOtherAccount ? (
            <div className="sso-state sso-state-center">
              <button
                type="button"
                className="sso-account-btn"
                disabled={busy}
                onClick={() => void continueAsAccount()}
              >
                <span className="sso-account-avatar" aria-hidden="true">
                  {casdoorAccount.displayName.slice(0, 1)}
                </span>
                <span className="sso-account-who">
                  <b>{casdoorAccount.displayName}</b>
                  <small>{casdoorAccount.name}</small>
                </span>
                <span className="sso-account-action">{busy ? '登录中…' : '继续'}</span>
              </button>
              <button
                type="button"
                className="sso-other-account"
                disabled={busy}
                onClick={() => setShowOtherAccount(true)}
              >
                使用其它账户登录
              </button>
            </div>
          ) : showDirectForm ? (
            <div className="sso-state">
              {casdoorAccount !== null ? (
                <button
                  type="button"
                  className="sso-other-account"
                  onClick={() => {
                    setShowOtherAccount(false);
                    setCasdoorAccount(null);
                  }}
                >
                  ← 返回 {casdoorAccount.displayName} 的会话
                </button>
              ) : null}
              <label className="auth-field">
                <span>账号</span>
                <input
                  value={ssoUser}
                  onChange={(e) => setSsoUser(e.target.value)}
                  autoComplete="username"
                  placeholder="统一身份用户名"
                  autoFocus
                  required
                />
              </label>
              <label className="auth-field">
                <span>密码</span>
                <input
                  type="password"
                  value={ssoPass}
                  onChange={(e) => setSsoPass(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                />
              </label>
              <button
                type="button"
                className="auth-submit"
                disabled={busy}
                onClick={() => void submitDirect()}
              >
                {busy ? '验证中…' : '继续'}
              </button>
            </div>
          ) : showSuccess ? (
            <div className="sso-state sso-state-center">
              <div className="auth-success">
                <span className="auth-success-title">登录成功</span>
                <span className="auth-success-sub">欢迎回来，{done.displayName}</span>
              </div>
              {done.desktop ? (
                <>
                  <div className="auth-success-note">桌面端将自动接续；若没有跳回，点下方按钮。</div>
                  <a className="btn-line primary sso-state-btn" href="uplab://sso/return">
                    返回桌面端
                  </a>
                </>
              ) : (
                <>
                  <div className="auth-success-note">{secondsLeft} 秒后自动进入系统…</div>
                  <a className="btn-line primary sso-state-btn" href="/">
                    立即进入系统
                  </a>
                </>
              )}
            </div>
          ) : (
            <div className="sso-state sso-state-center">
              <span className="sso-spinner" aria-hidden="true" />
              <span className="sso-pending-text">
                {isCallback ? '正在完成登录…' : '正在跳转到统一身份平台…'}
              </span>
            </div>
          )}
        </div>

        {/* ── 页脚：次级出口统一收在分隔线下的页脚区 ── */}
        <div className="sso-foot">
          {showDirectForm && (casdoorAccount === null || showOtherAccount) ? (
            <a href="/">使用账号密码登录</a>
          ) : null}
          {showSuccess && done.desktop ? <a href="/">进入网页版</a> : null}
        </div>
      </div>
    </div>
  );
}
